import { useEffect, useLayoutEffect, useCallback, useRef, useState, type ReactNode } from 'react';
import { Radio, Activity, CheckCircle2, XCircle, Layers, ClipboardList, RotateCcw, Truck, Droplets, MapPinned, FileDown, ClipboardCheck, Lock, Bot, MapPin, MoreVertical, Clock, BookMarked } from 'lucide-react';
import {
  elapsedSec,
  formatElapsed,
  STAGE_LABELS,
  CONFIRMATION_LABELS,
  INCIDENT_STAGES,
  INCIDENT_TYPE_LABELS,
  activeIncidentTypes,
  type Incident,
  type AuditEvent,
  type Insight,
} from '@duty/shared';
import type { PulseState } from '@/components/Pulse';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SettingsMenu } from '@/components/SettingsMenu';
import { RoleSwitcher } from '@/components/RoleSwitcher';
import { ExportPanel } from '@/components/ExportPanel';
import { ReviewPanel } from '@/components/ReviewPanel';
import { useRole } from '@/auth/role';
import { LocationSearch, type LocationResult } from '@/components/LocationSearch';
import { AssistantRail } from '@/components/AssistantRail';
import { InsightStream } from '@/components/InsightStream';
import { StageStepper } from '@/components/StageStepper';
import { ItascCapture } from '@/components/ItascCapture';
import { ItascSummary } from '@/components/ItascSummary';
import { ResourcesPanel } from '@/components/ResourcesPanel';
import { WaterPanel } from '@/components/WaterPanel';
import { LocalKnowledgePanel } from '@/components/LocalKnowledgePanel';
import { RequirementsPanel } from '@/components/RequirementsPanel';
import { KnowledgeBasePanel } from '@/components/KnowledgeBasePanel';
import { SitrepPanel } from '@/components/SitrepPanel';
import { MapPanel } from '@/components/MapPanel';
import { ResponseCoverageBar } from '@/components/response-coverage/ResponseCoverageBar';
import { IncidentDashboard } from '@/components/IncidentDashboard';
import { useNow } from '@/hooks/useNow';
import { useInsightAlerts } from '@/hooks/useInsightAlerts';
import { useIncidentStore } from '@/incident/store';
import { getHealth, type HealthResponse } from '@/api/client';

/** e.g. "Vehicle fire" or "Vehicle fire + Grass / bush fire" once a second type is added. */
const incidentTypeChipLabel = (incident: Incident): string =>
  activeIncidentTypes(incident).map((t) => INCIDENT_TYPE_LABELS[t]).join(' + ');

function useBreakpoint(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

export function App() {
  const { incident } = useIncidentStore();
  if (!incident) return <IncidentDashboard />;
  return <Workspace incident={incident} />;
}

function Workspace({ incident }: { incident: Incident }) {
  const { dispatch, insights, audit, returnToDashboard } = useIncidentStore();
  const { can } = useRole();
  const canEdit = can('incident.edit');
  const canManageConfig = can('config.manage');
  const now = useNow();
  const elapsed = elapsedSec(incident.clock, now);
  const [editingLocation, setEditingLocation] = useState(false);

  const isDesktop = useBreakpoint('(min-width: 1024px)');
  const isWide = useBreakpoint('(min-width: 1400px)');

  const activeInsights = insights.filter(
    (i) => i.status === 'open' || i.status === 'acknowledged',
  );
  const hasCritical = activeInsights.some((i) => i.severity === 'critical' && i.status === 'open');
  useInsightAlerts(insights);
  const pulseState: PulseState = hasCritical ? 'advising' : activeInsights.length > 0 ? 'analysing' : 'idle';

  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [healthError, setHealthError] = useState(false);
  useEffect(() => {
    const ctrl = new AbortController();
    getHealth(ctrl.signal)
      .then(setHealth)
      .catch(() => setHealthError(true));
    return () => ctrl.abort();
  }, []);

  const insightBadge = activeInsights.length > 0 ? (
    <span className="ml-1 rounded-full bg-accent/15 px-1.5 text-[10px] text-accent">
      {activeInsights.length}
    </span>
  ) : null;

  return (
    <div className="flex h-full flex-col">
      {isDesktop ? (
        <>
          <header className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-border bg-surface/70 px-4 py-2.5 backdrop-blur">
            <div className="flex items-center gap-2">
              <Radio size={18} className="text-accent" />
              <span className="font-semibold">{incident.name}</span>
              <span className="chip">{incidentTypeChipLabel(incident)}</span>
            </div>
            <HudStat label="Status" value={CONFIRMATION_LABELS[incident.status]} />
            <HudStat label="Stage" value={STAGE_LABELS[incident.stage]} />
            <HudStat label="Elapsed" value={formatElapsed(elapsed)} mono />
            {editingLocation ? (
              <div className="w-56">
                <LocationSearch
                  value={incident.location}
                  autoFocus
                  onChange={(result: LocationResult | string) => {
                    const location = typeof result === 'string' ? result : result.place;
                    const coordinates = typeof result === 'string' ? undefined : result.coordinates;
                    if (location.trim()) dispatch({ type: 'set-location', location: location.trim(), coordinates });
                    setEditingLocation(false);
                  }}
                  placeholder="Update location…"
                />
              </div>
            ) : (
              <button
                onClick={() => canEdit && setEditingLocation(true)}
                className={`leading-tight text-left ${canEdit ? 'group cursor-pointer' : 'cursor-default'}`}
                title={canEdit ? 'Click to update location' : undefined}
                aria-label={canEdit ? 'Edit incident location' : undefined}
              >
                <div className="text-[10px] uppercase tracking-wide text-muted">Location</div>
                <div className="flex items-center gap-1 text-sm">
                  {incident.location}
                  {canEdit && <MapPin size={11} className="shrink-0 text-muted opacity-0 transition-opacity group-hover:opacity-100" />}
                </div>
              </button>
            )}
            <div className="ml-auto flex items-center gap-3">
              <button
                onClick={returnToDashboard}
                className="inline-flex items-center gap-1 text-xs text-muted hover:text-accent"
                title="Back to dashboard — this incident stays open, reachable from the incident list"
              >
                <RotateCcw size={13} /> Dashboard
              </button>
              <ApiStatus health={health} healthError={healthError} />
              <RoleSwitcher />
              <ThemeToggle />
              <SettingsMenu />
            </div>
          </header>

          <StageStepper incident={incident} />
        </>
      ) : (
        <MobileTopBar
          incident={incident}
          elapsed={elapsed}
          canEdit={canEdit}
          editingLocation={editingLocation}
          setEditingLocation={setEditingLocation}
          health={health}
          healthError={healthError}
        />
      )}

      <ResponseCoverageBar incident={incident} />

      {!isDesktop ? (
        /* ── Mobile: four-panel swipe pager ────────────────────────────────── */
        <MobilePager
          incident={incident}
          canEdit={canEdit}
          canManageConfig={canManageConfig}
          insightCount={activeInsights.length}
          pulseState={pulseState}
          audit={audit}
          insights={insights}
        />
      ) : (
        /* ── Desktop: 3-col (lg) → 4-col (≥1400px) grid ───────────────────── */
        <main className={`grid min-h-0 flex-1 gap-3 p-3 ${
          isWide
            ? 'grid-cols-[20rem_minmax(0,1fr)_minmax(0,1fr)_22rem]'
            : 'grid-cols-[22rem_minmax(0,1fr)_22rem]'
        }`}>

          {/* Col 1 — Capture */}
          <section className="panel thin-scrollbar min-h-0 space-y-5 overflow-auto p-3.5" aria-label="Capture">
            {!canEdit && (
              <p className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-2/60 px-2.5 py-1.5 text-xs text-muted">
                <Lock size={13} /> Read-only view for your role — capture is disabled.
              </p>
            )}
            {canEdit && (
              <>
                <div>
                  <SectionTitle icon={<ClipboardList size={14} />}>Capture ITASC</SectionTitle>
                  <ItascCapture incident={incident} />
                </div>
                <div className="border-t border-border pt-4" aria-label="Responding resources">
                  <SectionTitle icon={<Truck size={14} />}>Responding resources</SectionTitle>
                  <ResourcesPanel incident={incident} />
                </div>
                <div className="border-t border-border pt-4" aria-label="Water plan">
                  <SectionTitle icon={<Droplets size={14} />}>Water plan</SectionTitle>
                  <WaterPanel incident={incident} />
                </div>
              </>
            )}
            <div className={canEdit ? 'border-t border-border pt-4' : ''} aria-label="Local knowledge">
              <SectionTitle icon={<MapPinned size={14} />}>Local knowledge</SectionTitle>
              <LocalKnowledgePanel incident={incident} />
            </div>
            <div className="border-t border-border pt-4" aria-label="Doctrine requirements">
              <SectionTitle icon={<BookMarked size={14} />}>Doctrine requirements</SectionTitle>
              <RequirementsPanel incident={incident} />
            </div>
            {canManageConfig && (
              <div className="border-t border-border pt-4" aria-label="Knowledge base">
                <SectionTitle icon={<BookMarked size={14} />}>Knowledge base</SectionTitle>
                <KnowledgeBasePanel />
              </div>
            )}
          </section>

          {/* Col 2 — SITREP + Map (+ Insights folded in when 3-col). One scroll
              region for the whole column, same as cols 1 and 3, so it doesn't
              read as visually shorter just because it also carries the map. */}
          <section className="thin-scrollbar min-h-0 space-y-3 overflow-auto">
            <div className="panel p-3.5" aria-label="Current ITASC">
              <SectionTitle icon={<Layers size={14} />}>Current ITASC</SectionTitle>
              <ItascSummary incident={incident} />
            </div>
            <div className="panel p-3.5" aria-label="SITREP generation">
              <SectionTitle icon={<Radio size={14} />}>SITREP &amp; CAD note</SectionTitle>
              <SitrepPanel incident={incident} />
            </div>
            <div className="panel p-3.5" aria-label="Handover and export">
              <SectionTitle icon={<FileDown size={14} />}>Handover &amp; export</SectionTitle>
              <ExportPanel incident={incident} />
            </div>
            {incident.stage === 'StandDown' && (
              <div className="panel p-3.5" aria-label="Post-incident review">
                <SectionTitle icon={<ClipboardCheck size={14} />}>Post-incident review</SectionTitle>
                <ReviewPanel incident={incident} />
              </div>
            )}
            {/* Insights live here only in 3-col mode */}
            {!isWide && (
              <div className="panel p-3.5" aria-label="Insight stream">
                <SectionTitle icon={<Activity size={14} />}>
                  Insights{insightBadge}
                </SectionTitle>
                <InsightStream />
              </div>
            )}
            <div className="h-56 shrink-0">
              <MapPanel incident={incident} />
            </div>
          </section>

          {/* Col 3 (4-col only) — Insights standalone column */}
          {isWide && (
            <section className="panel flex min-h-0 flex-col overflow-hidden p-3.5" aria-label="Insight stream">
              <SectionTitle icon={<Activity size={14} />}>
                Insights{insightBadge}
              </SectionTitle>
              <div className="thin-scrollbar min-h-0 flex-1 overflow-y-auto">
                <InsightStream />
              </div>
            </section>
          )}

          {/* Col 4 — Assistant rail */}
          <AssistantRail pulseState={pulseState} audit={audit} incident={incident} insights={insights} />
        </main>
      )}
    </div>
  );
}

/** Four-panel horizontal swipe pager for mobile/small screens. */
function MobilePager({
  incident,
  canEdit,
  canManageConfig,
  insightCount,
  pulseState,
  audit,
  insights,
}: {
  incident: Incident;
  canEdit: boolean;
  canManageConfig: boolean;
  insightCount: number;
  pulseState: PulseState;
  audit: AuditEvent[];
  insights: Insight[];
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(2); // Insights is the default landing panel

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const set = () => { el.scrollLeft = el.clientWidth * 2; };
    if (el.clientWidth > 0) {
      set();
    } else {
      requestAnimationFrame(set);
    }
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || el.clientWidth === 0) return;
    setActive(Math.round(el.scrollLeft / el.clientWidth));
  }, []);

  const scrollToPanel = (index: number) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ left: el.clientWidth * index, behavior: 'smooth' });
    setActive(index);
  };

  const NAV = [
    { label: 'ITASC', icon: <ClipboardList size={16} /> },
    { label: 'SITREP', icon: <Radio size={16} /> },
    { label: 'Insights', icon: <Activity size={16} />, badge: insightCount },
    { label: 'Duty', icon: <Bot size={16} /> },
  ] as const;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Scroll snap container */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="no-scrollbar flex min-h-0 flex-1 overflow-x-auto snap-x snap-mandatory overscroll-x-contain"
        aria-label="Incident panels"
      >
        {/* Panel 0 — ITASC Capture */}
        <div className="w-full shrink-0 snap-start space-y-4 overflow-y-auto p-3" role="region" aria-label="Capture">
          {!canEdit && (
            <p className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-2/60 px-2.5 py-1.5 text-xs text-muted">
              <Lock size={13} /> Read-only view for your role — capture is disabled.
            </p>
          )}
          {canEdit && (
            <>
              <div>
                <SectionTitle icon={<ClipboardList size={14} />}>Capture ITASC</SectionTitle>
                <ItascCapture incident={incident} />
              </div>
              <div className="border-t border-border pt-4">
                <SectionTitle icon={<Truck size={14} />}>Responding resources</SectionTitle>
                <ResourcesPanel incident={incident} />
              </div>
              <div className="border-t border-border pt-4">
                <SectionTitle icon={<Droplets size={14} />}>Water plan</SectionTitle>
                <WaterPanel incident={incident} />
              </div>
            </>
          )}
          <div className={canEdit ? 'border-t border-border pt-4' : ''}>
            <SectionTitle icon={<MapPinned size={14} />}>Local knowledge</SectionTitle>
            <LocalKnowledgePanel incident={incident} />
          </div>
          <div className="border-t border-border pt-4">
            <SectionTitle icon={<BookMarked size={14} />}>Doctrine requirements</SectionTitle>
            <RequirementsPanel incident={incident} />
          </div>
          {canManageConfig && (
            <div className="border-t border-border pt-4">
              <SectionTitle icon={<BookMarked size={14} />}>Knowledge base</SectionTitle>
              <KnowledgeBasePanel />
            </div>
          )}
        </div>

        {/* Panel 1 — SITREP + Map */}
        <div className="w-full shrink-0 snap-start space-y-3 overflow-y-auto p-3" role="region" aria-label="SITREP and CAD">
          <div className="panel p-3.5" aria-label="Current ITASC">
            <SectionTitle icon={<Layers size={14} />}>Current ITASC</SectionTitle>
            <ItascSummary incident={incident} />
          </div>
          <div className="panel p-3.5">
            <SectionTitle icon={<Radio size={14} />}>SITREP &amp; CAD note</SectionTitle>
            <SitrepPanel incident={incident} />
          </div>
          <div className="panel p-3.5">
            <SectionTitle icon={<FileDown size={14} />}>Handover &amp; export</SectionTitle>
            <ExportPanel incident={incident} />
          </div>
          {incident.stage === 'StandDown' && (
            <div className="panel p-3.5">
              <SectionTitle icon={<ClipboardCheck size={14} />}>Post-incident review</SectionTitle>
              <ReviewPanel incident={incident} />
            </div>
          )}
          <div className="h-52 overflow-hidden rounded-xl border border-border">
            <MapPanel incident={incident} />
          </div>
        </div>

        {/* Panel 2 — Insights (default landing panel) */}
        <div className="w-full shrink-0 snap-start overflow-y-auto p-3" role="region" aria-label="Insights">
          <SectionTitle icon={<Activity size={14} />}>
            Insights
            {insightCount > 0 && (
              <span className="ml-1 rounded-full bg-accent/15 px-1.5 text-[10px] text-accent">
                {insightCount}
              </span>
            )}
          </SectionTitle>
          <InsightStream />
        </div>

        {/* Panel 3 — Duty assistant */}
        <div className="flex h-full w-full shrink-0 snap-start flex-col" role="region" aria-label="Duty assistant">
          <AssistantRail pulseState={pulseState} audit={audit} incident={incident} insights={insights} />
        </div>
      </div>

      {/* Bottom navigation */}
      <nav className="flex shrink-0 border-t border-border bg-surface/80 backdrop-blur-sm" aria-label="Panel navigation">
        {NAV.map((item, i) => (
          <button
            key={item.label}
            onClick={() => scrollToPanel(i)}
            className={`relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ${
              active === i ? 'text-accent' : 'text-muted hover:text-text'
            }`}
            aria-label={item.label}
            aria-current={active === i ? 'page' : undefined}
          >
            <span className="relative">
              {item.icon}
              {'badge' in item && item.badge > 0 && (
                <span className="absolute -right-2 -top-1.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-accent px-0.5 text-[8px] font-medium text-white">
                  {item.badge > 9 ? '9+' : item.badge}
                </span>
              )}
            </span>
            <span className="leading-none">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

/**
 * Compact fixed top bar for mobile/small screens. Keeps the "fixed" chrome to a
 * small fraction of the viewport (≈3 short rows) so the panel content below gets
 * the height it needs: an identity row, a one-line meta strip (status · elapsed ·
 * editable location), and a single horizontally-scrollable stage strip. Secondary
 * controls (close / role / theme / experience) live in an overflow menu.
 */
function MobileTopBar({
  incident,
  elapsed,
  canEdit,
  editingLocation,
  setEditingLocation,
  health,
  healthError,
}: {
  incident: Incident;
  elapsed: number;
  canEdit: boolean;
  editingLocation: boolean;
  setEditingLocation: (v: boolean) => void;
  health: HealthResponse | null;
  healthError: boolean;
}) {
  const { dispatch, returnToDashboard } = useIncidentStore();
  const currentIdx = INCIDENT_STAGES.indexOf(incident.stage);
  const activeStageRef = useRef<HTMLButtonElement>(null);

  // Keep the current stage chip visible as the incident progresses — otherwise
  // late stages (Overhaul / Investigation / Stand-down) sit off the right edge.
  useEffect(() => {
    activeStageRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [currentIdx]);

  return (
    <div className="shrink-0 border-b border-border bg-surface/70 backdrop-blur">
      {/* Identity row */}
      <div className="flex items-center gap-2 px-3 pt-2">
        <Radio size={16} className="shrink-0 text-accent" />
        <span className="min-w-0 flex-1 truncate font-semibold">{incident.name}</span>
        <span className="chip shrink-0 whitespace-nowrap">{incidentTypeChipLabel(incident)}</span>
        <MobileMenu health={health} healthError={healthError} onClose={returnToDashboard} />
      </div>

      {/* Meta strip — elapsed · status · location (editable) */}
      <div className="flex items-center gap-2 px-3 py-1 text-[11px] text-muted">
        <Clock size={11} className="shrink-0" aria-hidden />
        <span className="shrink-0 font-mono">{formatElapsed(elapsed)}</span>
        <span aria-hidden>·</span>
        <span className="shrink-0 whitespace-nowrap text-text/80">{CONFIRMATION_LABELS[incident.status]}</span>
        <span aria-hidden>·</span>
        {editingLocation ? (
          <div className="min-w-0 flex-1">
            <LocationSearch
              value={incident.location}
              autoFocus
              onChange={(result: LocationResult | string) => {
                const location = typeof result === 'string' ? result : result.place;
                const coordinates = typeof result === 'string' ? undefined : result.coordinates;
                if (location.trim()) dispatch({ type: 'set-location', location: location.trim(), coordinates });
                setEditingLocation(false);
              }}
              placeholder="Update location…"
            />
          </div>
        ) : (
          <button
            onClick={() => canEdit && setEditingLocation(true)}
            className="flex min-w-0 flex-1 items-center gap-1 text-left"
            title={canEdit ? 'Edit incident location' : undefined}
            aria-label={canEdit ? 'Edit incident location' : undefined}
          >
            <span className="truncate">{incident.location}</span>
            {canEdit && <MapPin size={10} className="shrink-0 text-muted" />}
          </button>
        )}
      </div>

      {/* Stage strip — single scrollable row of tappable stages */}
      <div
        className="no-scrollbar flex items-center gap-1 overflow-x-auto px-3 pb-2"
        aria-label="Incident stage"
      >
        {INCIDENT_STAGES.map((stage, i) => {
          const state = i < currentIdx ? 'done' : i === currentIdx ? 'current' : 'todo';
          return (
            <button
              key={stage}
              ref={state === 'current' ? activeStageRef : undefined}
              onClick={() => dispatch({ type: 'set-stage', stage })}
              aria-current={state === 'current' ? 'step' : undefined}
              className={`shrink-0 whitespace-nowrap rounded-md px-2 py-1 text-xs transition-colors ${
                state === 'current'
                  ? 'bg-accent/20 font-semibold text-accent'
                  : state === 'done'
                    ? 'text-text/70'
                    : 'text-muted'
              }`}
            >
              {STAGE_LABELS[stage]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Overflow menu for secondary header controls on mobile (close / role / theme / experience). */
function MobileMenu({
  health,
  healthError,
  onClose,
}: {
  health: HealthResponse | null;
  healthError: boolean;
  onClose: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="More controls"
        className="flex items-center justify-center rounded-md p-1 text-muted hover:text-accent"
      >
        <MoreVertical size={18} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1.5 w-52 space-y-3 rounded-lg border border-border bg-surface p-3 shadow-lg"
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">API</span>
            <ApiStatus health={health} healthError={healthError} />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">Role</span>
            <RoleSwitcher />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">Theme</span>
            <ThemeToggle />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">Experience</span>
            <SettingsMenu />
          </div>
          <button
            onClick={() => { setOpen(false); onClose(); }}
            className="flex w-full items-center gap-1.5 border-t border-border pt-2.5 text-xs text-muted hover:text-accent"
          >
            <RotateCcw size={13} /> Back to dashboard
          </button>
        </div>
      )}
    </div>
  );
}

function HudStat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="leading-tight">
      <div className="text-[10px] uppercase tracking-wide text-muted">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

function ApiStatus({ health, healthError }: { health: HealthResponse | null; healthError: boolean }) {
  if (healthError) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-muted"
        title="API not reachable (expected without the SWA CLI/API running)"
      >
        <XCircle size={14} className="text-med" /> API offline
      </span>
    );
  }
  if (!health) return <span className="text-xs text-muted">checking API…</span>;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted" title={`v${health.version}`}>
      <CheckCircle2 size={14} className="text-ok" /> API ok · AI {health.aiEnabled ? 'on' : 'off'}
    </span>
  );
}

function SectionTitle({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <h2 className="mb-2.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
      {icon}
      {children}
    </h2>
  );
}
